import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Button, Space, DatePicker, Table, Modal } from 'antd';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { DownOutlined } from "@ant-design/icons";
import IndeterminateCheckBoxRoundedIcon from '@mui/icons-material/IndeterminateCheckBoxRounded';
import AddBoxRoundedIcon from '@mui/icons-material/AddBoxRounded';
import CustomTable from '../../CustomTable';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import ModeEditRoundedIcon from '@mui/icons-material/ModeEditRounded';
import HistoryIcon from '@mui/icons-material/History';
import DownloadDoneIcon from '@mui/icons-material/DownloadDone';
import Inventories from './Inventories';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { mainAxios } from '../../middleware/interceptor';
import { toast } from 'react-toastify';
import PrintInventoryInfo from '../PrintInventoryInfo';
import ExcelJS from "exceljs";
import PrintInventoryInfoTwo from '../PrintInventoryInfoTwo';
import dayjs from 'dayjs';

function InventoriesInfo({
  setInventoriesDetails,
  CountSheetId,
  CountSheetName,
  InventoryDate,
  inventoryDetails,
  restId,
  handleAfterUpdate
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [summaryData, setSummaryData] = useState([]);
  const [summaryBackupData, setSummaryBackupData] = useState([]);
  const [showReopenModal, setShowReopenModal] = useState(false);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [printData, setPrintData] = useState([]);
  const [showPrintElement, setShowPrintElement] = useState(false);

  const printRef = useRef();
  const printRefTwo = useRef();

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const NumberFormatter = new Intl.NumberFormat("en-US", {
    currency: "USD",
    maximumFractionDigits: 2,
  });

  const handleExcelDownload = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("InventoryDetails");

    const excelData = summaryData.map(item =>
      item.SubCatList.map(item2 =>
        item2.ProductList.map(item3 => ({
          "Category": item3.CatName,
          "Sub Category": item3.SubCatName,
          "Product": item3.Product,
          "Common Name": item3.SubProduct,
          "Prev Count": NumberFormatter.format(item3.PreCount),
          "Prev Value": currencyFormatter.format(item3.PreAmount),
          "Count": NumberFormatter.format(item3.Count),
          "Value": currencyFormatter.format(item3.Amount),
          "Change": currencyFormatter.format(item3.DiffAmount),
          "Report By": item3.ReportBy ?? item3.Unit
        }))
      )).flat(2);

    const headerRow = worksheet.addRow([
      "Category",
      "Sub Category",
      "Product",
      "Common Name",
      "Report By",
      "Prev Count",
      "Prev Value",
      "Count",
      "Value",
      "Change",
    ]);
    headerRow.font = { bold: true };

    excelData.forEach((item) => {
      worksheet.addRow([
        item["Category"],
        item["Sub Category"],
        item["Product"],
        item["Common Name"],
        item["Report By"],
        item["Prev Count"],
        item["Prev Value"],
        item["Count"],
        item["Value"],
        item["Change"],
      ]);
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = "InventoryDetails.xlsx";
      link.click();
    });
  };

  const items = [
    {
      key: "1",
      label: 'Export',
      onClick: handleExcelDownload
    },

    {
      key: "2",
      label: 'Print PDF',
      onClick: () => setShowPrintElement(true)
    },
  ];

  const tableProps = {
    muiExpandButtonProps: ({ row }) => ({
      children: row.getIsExpanded() ? <IndeterminateCheckBoxRoundedIcon fontSize='small' /> : <AddBoxRoundedIcon fontSize='small' />,
    }),
    muiExpandAllButtonProps: ({ table }) => ({
      children: table.getIsAllRowsExpanded() ? <IndeterminateCheckBoxRoundedIcon fontSize='small' /> : <AddBoxRoundedIcon fontSize='small' />,
    }),
    displayColumnDefOptions: {
      'mrt-row-expand': {
        size: 10,
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'end',
          alignItems: 'center',
          alignSelf: 'center'
        }}
      >
        <div style={{ display: 'flex', gap: '10px' }}>
          <div>
            <Button
              // style={{ backgroundColor: '#2BAB47', color: '#fff' }}
              className='printButton'
              icon={<PrintRoundedIcon style={{ fontSize: '18px' }} />}
              onClick={() => { GetInventoryDetails() }}
              disabled={isLoading}
            >
              Print Count
            </Button>
          </div>
          <div>
            <Dropdown
              menu={{ items }}
              trigger={['click']}
              disabled={isLoading || summaryData.length === 0}
            >
              <Button className='printDropdown' icon={<FileDownloadRoundedIcon style={{ fontSize: '18px' }} />}>
                <Space>
                  Export
                  <DownOutlined />
                </Space>
              </Button>
            </Dropdown>
          </div>
        </div>
      </div>
    )
  };

  const columns = useMemo(() => [
    {
      accessorKey: "CatName",
      header: "Category",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
      Cell: ({ row }) => (
        <span style={{ fontWeight: row.getCanExpand() ? "bold" : "normal" }}>
          {row.original.CatName} {!!row?.original?.SubCatList && `(${row?.original?.SubCatList?.length})`}
        </span>
      ),
    },
    {
      accessorKey: "SubCatName",
      header: "Sub Category",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      Cell: ({ row }) => (
        <span style={{ fontWeight: row.getCanExpand() ? "bold" : "normal" }}>
          {row.original.SubCatName} {!!row?.original?.ProductList && `(${row?.original?.ProductList?.length})`}
        </span>
      ),
    },
    {
      accessorKey: "Product",
      header: "Product",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
    },
    {
      accessorKey: "SubProduct",
      header: "Common Name",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
    },
    {
      accessorKey: "ReportBy",
      header: "Report By",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
    },
    {
      accessorKey: "PreCount",
      header: "Prev Count",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
    },
    {
      accessorKey: "PreAmount",
      header: "Prev Value",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ row }) => (
        <span style={{ fontWeight: row.getCanExpand() ? "bold" : "normal" }}>
          {currencyFormatter.format(row.original.PreAmount)}
        </span>
      ),
    },
    {
      accessorKey: "Count",
      header: "Count",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "left",
      },
    },
    {
      accessorKey: "Amount",
      header: "Value",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ row }) => (
        <span style={{ fontWeight: row.getCanExpand() ? "bold" : "normal" }}>
          {currencyFormatter.format(row.original.Amount)}
        </span>
      ),
    },
    {
      accessorKey: "DiffAmount",
      header: "Change",
      size: 100,
      muiTableHeadCellProps: {
        align: "left",
      },
      muiTableBodyCellProps: {
        align: "right",
      },
      Cell: ({ row }) => (
        <span style={{ fontWeight: row.getCanExpand() ? "bold" : "normal" }}>
          {row.original.DiffAmount < 0
            ? `(${currencyFormatter.format(row.original.DiffAmount).replace('-', '')})`
            : currencyFormatter.format(row.original.DiffAmount)
          }
        </span>
      ),
    },
  ], []);

  const GetInventoryDetails = () => {
    let req = {
      "RestId": restId,
      "Inventorykey": inventoryDetails?.InventoryId,
      "page_status": 3
    };

    mainAxios.post(`api/inventory`, req).then((res) => {
      if (res?.data?.Status === "1") {
        let temp = res?.data?.Data?.InventoryDetails?.map((a) => ({
          ...a,
          GroupedItems: a.GroupedItems.map((b) => ({
            ...b,
            productList: b.productList.map((c) => ({
              ...c,
              subproductlist: c.subproductlist.map((d) => ({
                ...d,
                subProductUnit: !!d.itemsList[0].BaseUnit ? d.itemsList[0].BaseUnit : d.itemsList[0].Unit,
                id: d.itemsList[0].id,
                Inventory_date: d.itemsList[0].Inventory_date,
                location: d.itemsList[0].location,
                Name: d.itemsList[0].Name,
                subProductPrice: d.itemsList[0].Price,
                Purveyor: d.itemsList[0].Purveyor,
                Unit: d.itemsList[0].Unit,
                CountSplit: d.itemsList[0].CountSplit,
                BaseUnit: d.itemsList[0].BaseUnit,
                SubCategoryId: d.itemsList[0].SubCategoryId,
                SubCatName: d.itemsList[0].SubCatName,
                Product: d.itemsList[0].Product,
                ProductId: d.itemsList[0].ProductId,
                Count: d.itemsList[0].Count,
                Amount: d.itemsList[0].Amount,
                PreAmount: d.itemsList[0].PreAmount,
                PreCount: d.itemsList[0].PreCount,
              })),
            })),
          })),
        }));

        setPrintData(temp);
      } else {
        setPrintData([]);
      }
    }).catch((err) => {
      console.log(err);
      setPrintData([]);
    })
  };

  const handleInit = async () => {
    const payload = {
      RestId: restId,
      Inventorykey: inventoryDetails?.InventoryId,
      page_status: 1
    };

    setIsLoading(true);
    await mainAxios.post(`api/Inventory`, payload).then((res) => {
      if (res.data?.Status === '1') {
        const data = res.data?.Data?.InventoryDetails.map(item => ({
          ...item,
          SubCatName: '',
          SubProduct: '',
          Product: '',
          ReportBy: '',
          SubCatList: item.SubCatList.map(subItem => ({
            ...subItem,
            CatName: item.CatName,
            SubProduct: '',
            Product: '',
            ReportBy: '',
            ProductList: subItem.ProductList.map(prodItem => ({
              ...prodItem,
              CatName: item.CatName,
              SubCatName: subItem.SubCatName,
              ReportBy: prodItem?.BaseUnit || prodItem?.Unit,
            }))
          }))
        })).filter(item => item.SubCatList.length > 0);

        setSummaryData(data);
        setSummaryBackupData({
          ...res.data?.Data,
          InventoryDetails: data
        });
        setIsLoading(false);
      } else {
        setSummaryData([]);
        setSummaryBackupData([]);
        setIsLoading(false);
      }
    }).catch((err) => {
      setSummaryData([]);
      setSummaryBackupData([]);
      setIsLoading(false);
      console.error(err);
    });
  };

  const handleCloseInventory = async () => {
    const payload = {
      RestId: restId,
      Date: inventoryDetails?.InventoryId,
    };

    await mainAxios.post(`api/Inventory/status`, payload).then((res) => {
      if (res.data.status === '1') {
        toast.success('Inventory closed successfully');
        handleAfterUpdate(inventoryDetails?.Id);
        // setShowCloseModal(false);
      } else {
        toast.error('Failed to close the inventory');
        // setShowCloseModal(false);
      }
    }).catch((err) => {
      console.error(err);
      toast.error('Failed to close the inventory');
      // setShowCloseModal(false);
    })
  };

  const handleReopenInventory = async () => {
    const payload = {
      RestId: restId,
      Date: inventoryDetails?.InventoryId,
      Status: 0
    };

    await mainAxios.post(`api/Inventory/changeStatusofInventory`, payload).then((res) => {
      if (res.data.status === 1) {
        toast.success('Inventory status updated successfully');
        handleAfterUpdate(inventoryDetails?.Id);
        setInventoriesDetails(3);
        setShowReopenModal(false);
      } else {
        toast.error('Failed to update the inventory status');
        setShowReopenModal(false);
      }
    }).catch((err) => {
      console.error(err);
      toast.error('Failed to update the inventory status');
      setShowReopenModal(false);
    })
  };

  useEffect(() => {
    handleInit();
  }, [inventoryDetails]);

  useEffect(() => {
    if (printData?.length > 0 && printRef.current) {
      setTimeout(() => { printRef.current.handlePrint(); }, 500);
      setTimeout(() => { setPrintData([]) }, 1500);
    }
  }, [printData]);

  useEffect(() => {
    if (showPrintElement) {
      setTimeout(() => { printRefTwo.current.handlePrint(); }, 500);
      setTimeout(() => { setShowPrintElement(false) }, 1500);
    }
  }, [showPrintElement]);

  return (
    <>
      <div>
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', }}>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'start', marginBottom: '10px' }}>
              <Button
                // style={{ padding: 0, border: 'none', boxShadow: 'none' }}
                onClick={() => { setInventoriesDetails(1) }}
                className='backButton'
                title='Back'
              >
                <ChevronLeftIcon style={{ marginBottom: '5px' }} />
              </Button>
              <h5>{dayjs(InventoryDate).format('MM/DD/YYYY')} "{CountSheetName}" Count</h5>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {inventoryDetails.Status === "Closed"
                ? (
                  <>
                    <div>
                      <Button
                        className='printButton2'
                        // icon={<ModeEditRoundedIcon style={{ fontSize: '18px' }} />}
                        onClick={() => setShowReopenModal(true)}
                      >
                        Reopen Inventory
                      </Button>
                    </div>
                    {/* <div>
                      <Button style={{ backgroundColor: '#2BAB47', color: '#fff' }}>
                        Edit Inventory Date
                      </Button>
                    </div> */}
                  </>
                )
                : (
                  <>
                    <div>
                      <Button
                        className='Deactivate-Button'
                        // icon={<DownloadDoneIcon style={{ fontSize: '18px' }} />}
                        onClick={() => handleCloseInventory()}//setShowCloseModal(true)}
                      >
                        Close Inventory
                      </Button>
                      {false &&
                        <Button style={{ backgroundColor: '#2BAB47', color: '#fff', border: 'none', fontWeight: '500' }}>
                          Edit Inventory Date
                        </Button>
                      }
                    </div>
                    <div>
                      <Button
                        className='printButton2'
                        // icon={<ModeEditRoundedIcon style={{ fontSize: '18px' }} />}
                        onClick={() => { setInventoriesDetails(3) }}
                      >
                        Edit Inventory
                      </Button>
                    </div>
                  </>
                )
              }
            </div>
          </div>
          {/* <hr style={{ margin: '0.5rem' }} /> */}
          <div style={{ marginTop: '20px' }} className='no_pinning'>
            <CustomTable
              data={summaryData}
              columns={columns}
              enableTopToolbar={true}
              enableColumnResizing={true}
              enableRowSelection={false}
              enableColumnActions={false}
              enablePagination={false}
              enableBottomToolbar={false}
              paginateExpandedRows={true}
              searchIconVisible={true}
              enableGlobalFilter={true}
              enableStickyHeader={true}
              enableGrouping={false}
              // groupedColumnMode={'remove'}
              initialState={{
                density: "compact",
                pagination: { psubProductSize: 10 },
                columnPinning: { left: ['mrt-row-expand', 'date'] },
                expanded: true
              }}
              isLoading={isLoading}
              enableExpanding={true}
              enableExpandAll={true}
              getSubRows={(row) => row.SubCatList || row.ProductList}
              optionalProps={tableProps}
            />
            <p style={{ textAlign: 'right', fontWeight: 'bold', margin: '10px 0' }}>
              Total Value: {currencyFormatter.format(summaryData.reduce((sum, cat) => sum + cat.Amount, 0))}
            </p>
          </div>
          {/* <hr /> */}
        </>
      </div>
      {printData.length > 0 && (
        <div style={{ position: 'absolute', top: -5000, left: -5000 }}>
          <PrintInventoryInfo
            ref={printRef}
            countSheetData={printData}
            CountSheetName={CountSheetName}
            InventoryDate={dayjs(InventoryDate).format('MM/DD/YYYY')}
          />
        </div>
      )}
      <div style={{ position: 'absolute', top: -5000, left: -5000 }}>
        <PrintInventoryInfoTwo
          ref={printRefTwo}
          countSheetData={
            summaryData.map(item =>
              item.SubCatList.map(item2 =>
                item2.ProductList.map(item3 => ({
                  "Category": item3.CatName,
                  "Sub Category": item3.SubCatName,
                  "Product": item3.Product,
                  "Common Name": item3.SubProduct,
                  "Prev Count": item3.PreCount,
                  "Prev Value": currencyFormatter.format(item3.PreAmount),
                  "Count": item3.Count,
                  "Value": currencyFormatter.format(item3.Amount),
                  "Change": currencyFormatter.format(item3.DiffAmount),
                  "Report By": item3.ReportBy ?? item3.Unit,
                }))
              )).flat(2)
          }
          CountSheetName={CountSheetName}
          InventoryDate={dayjs(InventoryDate).format('MM/DD/YYYY')}
        />
      </div>
      <Modal
        title='Reopen Inventory'
        open={showReopenModal}
        onCancel={() => setShowReopenModal(false)}
        closable
        width={400}
        footer={[
          <Button
            className='Deactivate-Button'
            onClick={() => setShowReopenModal(false)}
          >
            Cancel
          </Button>,
          <Button
            className='printButton2'
            onClick={() => { handleReopenInventory(inventoryDetails?.Id) }}
          >
            Confirm
          </Button>
        ]}
      >
        <div>Are you sure want to reopen the inventory?</div>
      </Modal>
      <Modal
        title='Close Inventory'
        open={showCloseModal}
        onCancel={() => setShowCloseModal(false)}
        closable
        width={400}
        footer={[
          <Button
            className='Deactivate-Button'
            onClick={() => setShowCloseModal(false)}
          >
            Cancel
          </Button>,
          <Button
            onClick={handleCloseInventory}
          >
            Confirm
          </Button>
        ]}
      >
        <div>Are you sure want to close the inventory?</div>
      </Modal>
    </>

  )
}

export default InventoriesInfo
